import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { Button } from '../../ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Label } from '../../ui/label';
import { DateTimeInput } from '../../ui/date-time-input';
import { refuseIfDateInputIncomplete } from '../../../lib/dateInputs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { cn } from '../../../lib/utils';
import type { Category, Heat, WOD } from '../../../types/heats';

/** Ce que le serveur accepte — tout optionnel, vide = toutes les heats planifiées. */
export interface AutoAssignScope {
  wod_ids?: string[];
  category_ids?: string[];
  heat_date?: string;
  time_from?: string;
  time_to?: string;
}

/** Une heat dans un de ces états porte des résultats : jamais touchée. */
const LOCKED = new Set(['preparing', 'countdown', 'running', 'finished', 'completed']);

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  heats: Heat[];
  wods: WOD[];
  categories: Category[];
  loading?: boolean;
  onConfirm: (scope: AutoAssignScope) => void;
}

/**
 * Auto-assignation à périmètre (2026-09-09, demande de Bryan).
 *
 * Avant, un seul bouton pour tout l'événement — et le serveur VIDAIT toutes
 * les assignations avant de replacer. Après un WOD éliminatoire, on veut
 * remplir le WOD 6 seul ; le samedi, remplir les heats de l'après-midi seules.
 * Le périmètre se lit ici : WOD, catégories, jour, plage horaire ; le nombre
 * de heats visées est calculé sur les mêmes règles que le serveur.
 */
const AutoAssignDialog: React.FC<Props> = ({ open, onOpenChange, heats, wods, categories, loading, onConfirm }) => {
  const { t } = useTranslation();
  const [wodId, setWodId] = useState<string>('all');
  const [categoryIds, setCategoryIds] = useState<Set<string>>(new Set());
  const [day, setDay] = useState<string>('all');
  const [timeFrom, setTimeFrom] = useState<string>('');
  const [timeTo, setTimeTo] = useState<string>('');

  const days = useMemo(
    () => Array.from(new Set(heats.map(h => h.date).filter(Boolean) as string[])).sort(),
    [heats]
  );

  const toggleCategory = (id: string) => {
    setCategoryIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const inScope = (h: Heat) => {
    if (wodId !== 'all' && h.wod_id !== wodId) return false;
    if (categoryIds.size > 0) {
      const cats = h.category_id ? [h.category_id] : [];
      if (!cats.some(c => categoryIds.has(c)) && !h.is_multi_category) return false;
    }
    if (day !== 'all' && h.date !== day) return false;
    const hm = (h.start_time || '').slice(0, 5);
    if (timeFrom && (!hm || hm < timeFrom)) return false;
    if (timeTo && (!hm || hm > timeTo)) return false;
    return true;
  };

  const targeted = heats.filter(h => !LOCKED.has((h.status || '').toLowerCase()) && inScope(h));
  const lockedInScope = heats.filter(h => LOCKED.has((h.status || '').toLowerCase()) && inScope(h)).length;

  const confirm = () => {
    if (refuseIfDateInputIncomplete(t)) return;
    const scope: AutoAssignScope = {};
    if (wodId !== 'all') scope.wod_ids = [wodId];
    if (categoryIds.size > 0) scope.category_ids = Array.from(categoryIds);
    if (day !== 'all') scope.heat_date = day;
    if (timeFrom) scope.time_from = timeFrom;
    if (timeTo) scope.time_to = timeTo;
    onConfirm(scope);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!loading) onOpenChange(o); }}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('assignments.autoAssignDialog.title')}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <p className="text-sm text-muted-foreground">{t('assignments.autoAssignDialog.description')}</p>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>{t('assignments.autoAssignDialog.scopeWod')}</Label>
              <Select value={wodId} onValueChange={setWodId}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('assignments.filters.allWods')}</SelectItem>
                  {[...wods].sort((a, b) => a.wod_number - b.wod_number).map(w => (
                    <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>{t('assignments.autoAssignDialog.scopeDay')}</Label>
              <Select value={day} onValueChange={setDay}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('assignments.autoAssignDialog.allDays')}</SelectItem>
                  {days.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-1">
            <Label>{t('assignments.autoAssignDialog.scopeCategories')}</Label>
            <div className="flex flex-wrap gap-2">
              {categories.map(c => {
                const active = categoryIds.size === 0 || categoryIds.has(c.id);
                return (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => toggleCategory(c.id)}
                    className={cn(
                      'rounded-md border px-3 py-1 text-xs transition-colors',
                      active ? 'border-primary/60 text-foreground' : 'border-border text-muted-foreground line-through'
                    )}
                  >
                    {c.name}
                  </button>
                );
              })}
            </div>
            <p className="text-xs text-muted-foreground">{t('assignments.autoAssignDialog.categoriesHint')}</p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>{t('assignments.autoAssignDialog.from')}</Label>
              <DateTimeInput type="time" value={timeFrom} onChange={e => setTimeFrom(e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label>{t('assignments.autoAssignDialog.to')}</Label>
              <DateTimeInput type="time" value={timeTo} onChange={e => setTimeTo(e.target.value)} />
            </div>
          </div>

          <p className="text-sm">
            {t('assignments.autoAssignDialog.heatsTargeted', { count: targeted.length })}
            {lockedInScope > 0 && (
              <span className="text-muted-foreground"> · {t('assignments.autoAssignDialog.lockedSkipped', { count: lockedInScope })}</span>
            )}
          </p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
            {t('common.cancel')}
          </Button>
          <Button onClick={confirm} disabled={loading || targeted.length === 0}>
            {loading && <Loader2 className="animate-spin" />}
            {t('assignments.autoAssignDialog.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AutoAssignDialog;
